import { cn } from '../../utils/cn';
import { MenuListItem } from '../MenuListItem';
import type { DropdownItemProps } from './Dropdown.types';
import { useDropdownContext } from './DropdownContext';

/**
 * One action row in a `<DropdownContent>` — a shared {@link MenuListItem}
 * (`accessibilityRole="menuitem"`) wired to the menu context: pressing it
 * closes the menu (unless `closeOnSelect={false}`) and fires `onSelect`.
 * The row size is inherited from the `<Dropdown size>` root.
 *
 * Cross-platform — the same file renders on web and native.
 */
export function DropdownItem({
  children,
  description,
  descriptionClassName,
  icon,
  variant,
  disabled,
  selected,
  showCheck,
  rightSlot,
  rightControl,
  as,
  webProps,
  onSelect,
  closeOnSelect = true,
  className,
  textClassName,
  testID,
}: DropdownItemProps) {
  const { close, size } = useDropdownContext('DropdownItem');

  return (
    <MenuListItem
      size={size}
      // The panel HUGS its content, and a `w-full` (width: 100%) row
      // contributes nothing to a parent's intrinsic width — so with it the
      // panel collapsed to `minWidth` and every label ellipsized regardless of
      // how much room was free. Width auto lets the longest row size the panel;
      // rows still fill it, because `DropdownMenu` is a flex column and
      // `align-items: stretch` is the default.
      fullWidth={false}
      description={description}
      descriptionClassName={descriptionClassName}
      variant={variant}
      disabled={disabled}
      selected={selected}
      showCheck={showCheck}
      leftIcon={icon}
      rightSlot={rightSlot}
      rightControl={rightControl}
      as={as}
      webProps={webProps}
      onPress={(event) => {
        if (closeOnSelect) close();
        onSelect?.(event);
      }}
      // Keep the row at its natural height — without this a flex-column
      // scroll container compresses rows to fit instead of overflowing.
      className={cn('shrink-0', className)}
      textClassName={textClassName}
      testID={testID}>
      {children}
    </MenuListItem>
  );
}
